import React, { useEffect } from "react";
import {
  decodeOpAccessToken,
  decodePlatformToken,
  decodeWarpAccessToken,
  updatePlatformTokenToLocalStorage,
} from "../utility";
import { popupAlert } from "../UI/Popups/popup";
import { getTokenAsync } from "../config";
import { useLayoutEffect } from "react";

const PlatformSessionMonitor = ({ children }) => {
  const [platformToken, setPlatformToken] = React.useState(false);

  useEffect(
    () => {
      if (!platformToken) {
        return;
      }
      let interval = null;
      let isSessionExpired = false;

      interval = setInterval(async () => {
        if (isSessionExpired) {
          return;
        }

        const tokenId = localStorage.getItem("tokenId");
        const isAuthentic = localStorage.getItem("IsAuthentic");
        const isAuthenticated = isAuthentic === "true";

        if (!isAuthenticated) {
          await checkAndRefreshToken(tokenId).then((newTokenId) => {
            setPlatformToken(newTokenId);
          });
          return;
        }

        // Authenticated user: only the user-identity tokens (warpToken,
        // opsToken) determine whether the session is still valid.
        // The platform tokenId is a short-lived service token used to call
        // the next-js API and must be silently refreshed when it expires —
        // its expiry is NOT a signal that the user's session has ended.
        if (isAuthenticated) {
          const opsToken = localStorage.getItem("opsToken");
          const warpToken = localStorage.getItem("warpToken");

          const platformDecoded = decodePlatformToken(tokenId);
          if (!platformDecoded) {
            try {
              await checkAndRefreshToken(tokenId);
            } catch (e) {
              // swallow; next tick will retry
            }
          }

          let opsTokenExpired = false;
          let warpTokenExpired = false;

          if (!!opsToken && opsToken !== "null") {
            opsTokenExpired = decodeOpAccessToken(opsToken) ? false : true;
          }

          if (!!warpToken && warpToken !== "null") {
            warpTokenExpired = decodeWarpAccessToken(warpToken) ? false : true;
          }

          if (opsTokenExpired || warpTokenExpired) {
            popupAlert(
              "UserSessionPopup",
              "Session Expired",
              "Your session has expired. Please log in again to continue.",
              logout,
              "",
              "Log In"
            );
            isSessionExpired = true;
          }
        }
      }, 2000);

      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    },
    [platformToken]
  );

  useLayoutEffect(() => {
    const tokenId = localStorage.getItem("tokenId");
    const platformTokenExpired = !decodePlatformToken(tokenId);
    const isAuthentic = localStorage.getItem("IsAuthentic");
    const isAuthenticated = isAuthentic === "true";

    if (platformTokenExpired && !isAuthenticated) {
      const refreshToken = async () => {
        try {
          const newTokenId = await checkAndRefreshToken(tokenId);
          setPlatformToken(newTokenId);
        } catch (error) {
          console.error("Error in token refresh:", error);
        }
      };
      refreshToken();
    } else {
      setPlatformToken(tokenId);
    }
  }, []);

  const logout = () => {
    localStorage.clear();
    window.location.href = "/";
  };

  return platformToken ? children : null;
};

export const checkAndRefreshToken = async (tokenId) => {
  const tokenInfo = decodePlatformToken(tokenId);

  if (tokenInfo === null) {
    try {
      const response = await getTokenAsync();
      const newTokenId = response.data.tokenId;
      updatePlatformTokenToLocalStorage(
        response.data.tokenId,
        response.data.expires_in
      );
      return newTokenId;
    } catch (error) {
      console.error("Error refreshing token:", error);
      return null;
    }
  }
  return tokenId;
};

export default PlatformSessionMonitor;
